import React, { useState, useRef } from "react";
import MainLayout from "../../Layout/mainLayout";
import { ReactComponent as Franchise } from "../../Global Icons/Franchise.svg";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import AddImage from "../../Global Functions/addImage";
import TextInput from "../../Global Functions/textInput";
// import TwoButtons from "../../Global Functions/buttons";
import { addNewsBrand } from "../../Redux/Actions/newsBrandAction";
import { useNavigate } from "react-router-dom";
import "./addNewsBrand.css";
// import { FaTrash } from "react-icons/fa";

const MAX_TITLE_LENGTH = 100;
const MAX_DESCRIPTION_LENGTH = 500;

const AddNewsBrand = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [formState, setFormState] = useState({
    title: "",
    company_name: "",
    url: "",
    brand_logo: "",
    description: "",
    type: "featured_brand",
    status: "active",
  });

  const [imagePreview, setImagePreview] = useState(null);
  const imageInputRef = useRef(null);

  const getCharCountClass = (length, max) => {
    if (length >= max) return "char-count error";
    if (length >= max * 0.8) return "char-count warning";
    return "char-count safe";
  };

  const getCharCountPercentage = (length, max) => {
    return Math.min((length / max) * 100, 100);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === "title" && value.length > MAX_TITLE_LENGTH) return;
    if (name === "description" && value.length > MAX_DESCRIPTION_LENGTH) return;

    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validTypes = ["image/png", "image/jpeg", "image/jpg"];
    if (!validTypes.includes(file.type)) {
      toast.error("Please upload an image in PNG, JPEG, or JPG format only.");
      event.target.value = "";
      return;
    }

    try {
      const uploadedUrl = await AddImage(file);
      if (uploadedUrl) {
        setFormState((prev) => ({ ...prev, brand_logo: uploadedUrl }));
        setImagePreview(uploadedUrl);
      }
    } catch {
      toast.error("Image upload failed");
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!formState.title.trim()) {
      return toast.error("Title is required.");
    }

    if (formState.title.length > MAX_TITLE_LENGTH) {
      return toast.error(`Title cannot exceed ${MAX_TITLE_LENGTH} characters.`);
    }

    // if (!formState.brand_logo) {
    //   return toast.error("Brand logo is required.");
    // }

    const payload = {
      title: formState.title.trim(),
      company_name: formState.company_name?.trim() || null,
      url: formState.url?.trim() || null,
      brand_logo: formState.brand_logo,
      description: formState.description.trim(),
      type: formState.type,
      status: formState.status,
    };

    try {
      const response = await dispatch(addNewsBrand(payload));

      if (response && (response.statusCode === 200 || response.status === "success")) {
        // toast.success("News Brand added successfully!");
        setFormState({
          title: "",
          company_name: "",
          url: "",
          brand_logo: "",
          description: "",
          type: "featured_brand",
          status: "active",
        });
        setImagePreview(null);

        if (imageInputRef.current) imageInputRef.current.value = "";
        navigate("/fa_brands");
      } else {
        toast.error(response.message || "Failed to add news brand");
      }
    } catch (error) {
      toast.error("Error submitting news brand");
    }
  };

  const CharCountDisplay = ({ current, max, fieldName }) => {
    const percentage = getCharCountPercentage(current, max);
    const statusClass = getCharCountClass(current, max);
    const isWarning = current >= max * 0.8 && current < max;
    const isError = current >= max;

    return (
      <div className={`char-count-container ${statusClass}`}>
        <div className="char-count-bar">
          <div
            className="char-count-fill"
            style={{
              width: `${percentage}%`,
              backgroundColor: isError ? "#d9534f" : isWarning ? "#f0ad4e" : "#5cb85c",
            }}
          />
        </div>
        <div className="char-count-text">
          <span className="char-count-current">{current}</span>
          <span className="char-count-separator">/</span>
          <span className="char-count-max">{max}</span>
          {isWarning && !isError && <span className="char-count-warning-icon">⚠️</span>}
          {isError && <span className="char-count-error-icon">✕</span>}
        </div>
      </div>
    );
  };

  return (
    <MainLayout>
      <div className="newsbrand-form-container">
        <div className="newsbrand-form-wrapper newsbrand-card">
          <div className="newsbrand-card-content">
            <div className="newsbrand-header">
              <div className="newsbrand-header-icon">
                <Franchise />
              </div>
              <h1>Add Featured/Associated Brand</h1>
            </div>

            {/* Title */}
            <div className="form-section">
              <TextInput
                label="Title"
                name="title"
                value={formState.title}
                onChange={handleChange}
                required
                inputClass="form-input"
                labelClass="form-label"
                maxLength={MAX_TITLE_LENGTH}
              />
              <CharCountDisplay
                current={formState.title.length}
                max={MAX_TITLE_LENGTH}
                fieldName="Title"
              />
            </div>

            {/* Company Name */}
            <div className="form-section">
              <TextInput
                label="Company Name"
                name="company_name"
                value={formState.company_name}
                onChange={handleChange}
                inputClass="form-input"
                labelClass="form-label"
              />
            </div>

            {/* URL */}
            <div className="form-section">
              <TextInput
                label="Website URL"
                name="url"
                value={formState.url}
                onChange={handleChange}
                inputClass="form-input"
                labelClass="form-label"
                type="url"
              />
            </div>

            {/* Description */}
            <div className="form-section">
              <TextInput
                label="Description"
                name="description"
                value={formState.description}
                onChange={handleChange}
                inputClass="form-input"
                labelClass="form-label"
                maxLength={MAX_DESCRIPTION_LENGTH}
                multiline
              />
              <CharCountDisplay
                current={formState.description.length}
                max={MAX_DESCRIPTION_LENGTH}
                fieldName="Description"
              />
            </div>

            <div className="form-section">
              <label className="form-label" htmlFor="type_select">
                Type <span style={{ color: "red" }}>*</span>
              </label>
              <select
                id="type_select"
                name="type"
                value={formState.type}
                onChange={handleChange}
                className="form-input"
              >
                <option value="featured_brand">Featured Brand</option>
                <option value="associated_brand">Associated Brand</option>
              </select>
            </div>

            {/* Status */}
            <div className="form-section">
              <label className="form-label" htmlFor="status_select">
                Status <span style={{ color: "red" }}>*</span>
              </label>
              <select
                id="status_select"
                name="status"
                value={formState.status}
                onChange={handleChange}
                className="form-input"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            {/* Brand Logo Upload */}
            <div className="form-section">
              <label className="form-label file-upload-area" htmlFor="brand_logo_input">
                Brand Logo <span style={{ color: "red" }}>*</span>
              </label>
              <input
                id="brand_logo_input"
                style={{ width: "97%" }}
                type="file"
                name="brand_logo"
                accept="image/png, image/jpeg, image/jpg"
                onChange={handleFileChange}
                className="form-input"
                ref={imageInputRef}
              />
              {imagePreview && (
                <img
                  src={imagePreview}
                  alt="Brand logo preview"
                  style={{
                    maxWidth: "200px",
                    maxHeight: "150px",
                    marginTop: 10,
                    borderRadius: "8px",
                    border: "1px solid #e5e7eb",
                  }}
                />
              )}
            </div>

            <div className="form-navigation">
              <button type="button" onClick={() => navigate("/fa_brands")}>
                Cancel
              </button>
              <button type="button" onClick={handleSubmit}>
                Save
              </button>
              {/* <TwoButtons onClick2={handleSubmit} buttonHead2="Save" /> */}
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default AddNewsBrand;
